import React, { Component } from 'react';
import { StyleSheet, View, TouchableOpacity, ScrollView } from 'react-native';
import { observer } from 'mobx-react';
import { range } from 'lodash';

import Text from '../shared/Text';
import Modal from '../shared/Modal';
import Button from '../shared/Button';
import Image from '../shared/Image';
import heroStore, { dressUndressThing } from '../../stores/hero';
import appStore from '../../stores/app';

import ThingItem from './ThingItem';

import { getThing, isArm } from '../../utils/thingUtils';
import { t, action } from '../../utils/appUtils';
import { IS_WEB } from '../../lib/appConstants';

import { thingImageRequire, warriorImageRequire } from '../../lib/imagesRequire';
import {
  getDressedThings,
  getDressedThingsByType,
  checkThingStability,
  getAppliedThings,
  thingCanBeApplied,
} from '../../utils/warriorUtils';
import { THING_TYPES } from '../../lib/constants';

import { PADDING, HP_HEIGHT, WINDOW_HEIGHT, RED, pOrT } from '../styleConstants';
import { WarriorThingType, WarriorType, ThingType } from '../../lib/types';

import logImport from '../../lib/log';
const log: any = logImport;

import { applyAction } from '../Inventory/Inventory';

let Tooltip: any;
if (IS_WEB) {
  ({ Tooltip } = require('react-tippy'));
}

const ARMS = 'arms';
const ARMS_SHIELD = 'armsShield';

const styles = StyleSheet.create({
  block: {
    position: 'absolute',
  },
  icon: {
    marginTop: 2,
    marginLeft: 2,
  },
  slotThing: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
  },
  thingLabelWrapper: {
    position: 'absolute',
    flexDirection: 'row',
    bottom: 2,
    left: 2,
    width: 12,
    height: 12,
  },
  thingLabelWrapperSmall: {
    width: 8,
    height: 8,
    bottom: 1,
    left: 1,
  },
  thingLabelLevelWrapper: {
    backgroundColor: 'black',
    width: 12,
    height: 12,
    borderRadius: 4,
    paddingLeft: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  thingLabelLevelWrapperSmall: {
    width: 8,
    height: 8,
    borderRadius: 3,
  },
  thingLabelLevelText: {
    color: 'white',
    fontSize: 8,
    marginLeft: IS_WEB ? -1 : 0,
    marginTop: IS_WEB ? -1 : 0,
    backgroundColor: 'transparent',
  },
  thingLabelLevelTextSmall: {
    fontSize: 6,
  },
  thingLabelType: {
    marginTop: 2,
    marginLeft: 2,
    width: 8,
    height: 8,
    borderRadius: 2,
  },
  thingLabelTypeSmall: {
    width: 6,
    height: 6,
    marginTop: 1,
    marginLeft: 1,
  },
  thingStabilityLabel: {
    backgroundColor: RED,
    position: 'absolute',
    right: 2,
    top: 2,
    width: 8,
    height: 8,
    borderRadius: 2,
  },
  thingInfoModal: {},
});

const MMAX_HEIGHT = IS_WEB ? 411 : pOrT(411, 390);
const SECONDARY_MAX_HEIGHT = 370;
const MAX_HEIGHT = IS_WEB ? SECONDARY_MAX_HEIGHT : MMAX_HEIGHT;
const MAX_BLOCK_DELTA = MMAX_HEIGHT / SECONDARY_MAX_HEIGHT;
export const HP_OFFSET = HP_HEIGHT + PADDING * 3;
export const FULL_MAX_HEIGHT = MAX_HEIGHT + HP_OFFSET;
export const FULL_WIDTH_DELTA = 1.268;

const FULL_HEIGHT = WINDOW_HEIGHT - HP_OFFSET;
export const FULL_WIDTH = pOrT(FULL_HEIGHT, MAX_HEIGHT) / FULL_WIDTH_DELTA;

const APPLIED = 'APPLIED';

export function getDynamicStyles(height: number = WINDOW_HEIGHT) {
  const FULL_HEIGHT1 = height - HP_OFFSET;
  const PROPORTION_DELTA = MAX_HEIGHT / FULL_HEIGHT1;

  const getProportionValue = (value: number) =>
    pOrT(value / PROPORTION_DELTA, value / MAX_BLOCK_DELTA);

  const WHEIGHT = pOrT(FULL_HEIGHT1, MAX_HEIGHT);
  const FULL_WIDTH1 = WHEIGHT / FULL_WIDTH_DELTA;

  const MAIN_SIZE = getProportionValue(64);
  const LEFT_OFFSET = getProportionValue(12);
  const TOP_OFFSET = getProportionValue(13);
  const WIDTH = MAIN_SIZE;
  const RIGHT = FULL_WIDTH1 - WIDTH - LEFT_OFFSET;
  const RING_WIDTH = MAIN_SIZE / 2;

  const IMAGE_WIDTH = getProportionValue(172);

  const ELIXIR_WIDTH_MINUS = getProportionValue(6);
  const ELIXIRS_WRAPPER_HEIGHT = WIDTH / 2;
  const ELIXIR_WIDTH = RING_WIDTH - ELIXIR_WIDTH_MINUS;
  const ELIXIRS_WRAPPER_WIDTH = FULL_WIDTH1 - LEFT_OFFSET * 2 - WIDTH * 2;

  return {
    WIDTH,
    RING_WIDTH,
    ELIXIR_WIDTH,
    getProportionValue,
    FULL_WIDTH: FULL_WIDTH1,
    ELIXIRS_WRAPPER_WIDTH,
    ELIXIRS_WRAPPER_HEIGHT,
    dynamicStyles: {
      wrapper: {
        width: FULL_WIDTH1,
        height: WHEIGHT,
      },
      [THING_TYPES.HELMET]: {
        top: TOP_OFFSET,
        left: LEFT_OFFSET,
        height: WIDTH,
        width: WIDTH,
      },
      [THING_TYPES.AMULET]: {
        top: TOP_OFFSET + WIDTH,
        left: LEFT_OFFSET,
        height: WIDTH,
        width: WIDTH,
      },
      [ARMS]: {
        top: TOP_OFFSET + WIDTH * 2,
        left: LEFT_OFFSET,
        height: WIDTH,
        width: WIDTH,
      },
      [THING_TYPES.GLOVES]: {
        top: TOP_OFFSET + WIDTH * 3,
        left: LEFT_OFFSET,
        height: WIDTH,
        width: WIDTH,
      },
      [THING_TYPES.ARMOR]: {
        top: TOP_OFFSET + WIDTH * 4,
        left: LEFT_OFFSET,
        height: WIDTH,
        width: WIDTH,
      },
      [THING_TYPES.PANTS]: {
        top: TOP_OFFSET + WIDTH * 5,
        left: LEFT_OFFSET,
        height: WIDTH,
        width: WIDTH,
      },
      [THING_TYPES.BRACER]: {
        top: TOP_OFFSET,
        left: RIGHT,
        height: WIDTH,
        width: WIDTH,
      },
      [THING_TYPES.CAPE]: {
        top: TOP_OFFSET + WIDTH,
        left: RIGHT,
        height: WIDTH,
        width: WIDTH,
      },
      [ARMS_SHIELD]: {
        top: TOP_OFFSET + WIDTH * 2,
        left: RIGHT,
        height: WIDTH,
        width: WIDTH,
      },
      rings: {
        position: 'absolute',
        top: TOP_OFFSET + WIDTH * 3,
        left: RIGHT,
      },
      [THING_TYPES.RING]: {
        height: RING_WIDTH,
        width: RING_WIDTH,
      },
      [THING_TYPES.BELT]: {
        top: TOP_OFFSET + WIDTH * 4,
        left: RIGHT,
        height: WIDTH,
        width: WIDTH,
      },
      [THING_TYPES.BOOTS]: {
        top: TOP_OFFSET + WIDTH * 5,
        left: RIGHT,
        height: WIDTH,
        width: WIDTH,
      },
      appliedWrapper: {
        top: TOP_OFFSET + WIDTH * 5,
      },
      elixirsWrapper: {
        position: 'absolute',
        top: TOP_OFFSET + WIDTH * 5 + ELIXIRS_WRAPPER_HEIGHT,
        left: LEFT_OFFSET + WIDTH,
        width: ELIXIRS_WRAPPER_WIDTH,
        height: ELIXIRS_WRAPPER_HEIGHT,
      },
      elixirsScroll: {
        paddingHorizontal: ELIXIR_WIDTH_MINUS / 2,
        paddingVertical: ELIXIR_WIDTH_MINUS / 2,
      },
      [THING_TYPES.ELIXIR]: {
        width: ELIXIR_WIDTH,
        height: ELIXIR_WIDTH,
      },
      slotBody: {
        left: WIDTH + LEFT_OFFSET,
        top: TOP_OFFSET,
        width: FULL_WIDTH1 - LEFT_OFFSET * 2 - WIDTH * 2,
        height: WHEIGHT - TOP_OFFSET * 2 - WIDTH,
      },
      avatar: {
        flex: 1,
        width: IMAGE_WIDTH,
      },
    },
  };
}

const defaultDynamicStyles = getDynamicStyles();

export function getSlotDimensions(type: string, dynamicStyles: any) {
  const { WIDTH, RING_WIDTH, ELIXIR_WIDTH } = dynamicStyles || defaultDynamicStyles;

  const dimensions = {
    height: WIDTH,
    width: WIDTH,
  };

  switch (type) {
    case THING_TYPES.RING:
      dimensions.height = RING_WIDTH;
      dimensions.width = RING_WIDTH;
      break;
    case THING_TYPES.ELIXIR:
    case THING_TYPES.SCROLL:
    case APPLIED:
      dimensions.height = ELIXIR_WIDTH;
      dimensions.width = ELIXIR_WIDTH;
      break;
    default:
  }

  return dimensions;
}

function getSlotStyles(type: string, ddynamicStyles: any) {
  const { dynamicStyles } = ddynamicStyles;

  return [(dynamicStyles as any)[type], styles.block];
}

function getThingBackImage(type: string) {
  switch (type) {
    case THING_TYPES.HELMET:
      return require('../../assets/images/helmetBack.png');
    case THING_TYPES.AMULET:
      return require('../../assets/images/amuletBack.png');
    case THING_TYPES.GLOVES:
      return require('../../assets/images/glovesBack.png');
    case THING_TYPES.ARMOR:
      return require('../../assets/images/armorBack.png');
    case THING_TYPES.PANTS:
      return require('../../assets/images/pantsBack.png');
    case THING_TYPES.BRACER:
      return require('../../assets/images/bracerBack.png');
    case THING_TYPES.CAPE:
      return require('../../assets/images/capeBack.png');
    case ARMS:
      return require('../../assets/images/armsBack.png');
    case ARMS_SHIELD:
      return require('../../assets/images/armsShieldBack.png');
    case THING_TYPES.RING:
      return require('../../assets/images/ringBack.png');
    case THING_TYPES.BELT:
      return require('../../assets/images/beltBack.png');
    case THING_TYPES.BOOTS:
      return require('../../assets/images/bootsBack.png');
    case THING_TYPES.ELIXIR:
      return require('../../assets/images/elixirBack.png');
  }
}

type ThingInfoModalProps = {
  warriorThing: WarriorThingType;
  warrior: WarriorType;
  onHide: Function;
  hideOverlay?: boolean;
  showApplyAction?: boolean;
};

const ThingInfoModal = ({
  warriorThing,
  warrior,
  onHide,
  hideOverlay,
  showApplyAction,
}: ThingInfoModalProps) => (
  <Modal
    style={styles.thingInfoModal}
    size={{
      width: pOrT(467, 550),
      height: pOrT(195, 220),
    }}
    noFrame
    onHide={onHide}
    hideOverlay={hideOverlay}
  >
    <ThingItem
      warrior={warrior}
      width={pOrT(467, 550)}
      warriorThingOrId={warriorThing}
      modalInfo
      actionsRenderer={
        showApplyAction
          ? (thing: ThingType) => (
              <Button
                onPress={() => applyAction(thing, warriorThing, onHide as () => void)}
                style={{ marginTop: 40 }}
                hitSlop={{
                  top: 0,
                  bottom: 0,
                }}
              >
                {t('apply').toUpperCase()}
              </Button>
            )
          : undefined
      }
    />
  </Modal>
);

type ThingLabelProps = {
  level: number;
  duplicateLvlImage?: number;
  small?: boolean;
};

const ThingLabel = ({ level = 0, duplicateLvlImage = 0, small }: ThingLabelProps) => (
  <View style={[styles.thingLabelWrapper, small && styles.thingLabelWrapperSmall]}>
    <View
      style={[styles.thingLabelLevelWrapper, small ? styles.thingLabelLevelWrapperSmall : null]}
    >
      <Text
        bold
        style={[styles.thingLabelLevelText, small ? styles.thingLabelLevelTextSmall : null]}
      >
        {level}
      </Text>
    </View>
    <View
      style={[
        {
          backgroundColor: ['transparent', '#999999', '#f2777a', '#6699cc'][duplicateLvlImage],
        },
        styles.thingLabelType,
        small && styles.thingLabelTypeSmall,
      ]}
    />
  </View>
);

type ThingImageProps = {
  dynamicStyles?: any;
  thing: ThingType;
  warriorThing: WarriorThingType;
  highlightWarning?: boolean;
  applied?: boolean;
};

export const ThingImage = ({
  warriorThing,
  thing,
  dynamicStyles,
  highlightWarning = true,
  applied,
}: ThingImageProps) => {
  const slotDimensions = getSlotDimensions(applied ? APPLIED : thing.type, dynamicStyles);

  return (
    <View
      style={{
        flex: 1,
        width: slotDimensions.width,
        position: 'relative',
      }}
    >
      <Image
        style={{
          height: slotDimensions.width,
          width: slotDimensions.height,
          position: 'absolute',
          left: 0,
        }}
        source={require('../../assets/images/greyBack.png')}
      />
      <Image
        style={{
          height: slotDimensions.width,
          width: slotDimensions.height,
          position: 'absolute',
        }}
        source={require('../../assets/images/armWrapper.png')}
      />
      <View
        style={{
          // flex: 1,
          justifyContent: 'center',
          alignContent: 'center',
          alignItems: 'center',
          height: slotDimensions.width,
          width: slotDimensions.height,
        }}
      >
        <Image
          style={{
            resizeMode: 'contain',
            width: slotDimensions.width - (IS_WEB ? 5 : slotDimensions.width * 0.2),
            height: slotDimensions.height - (IS_WEB ? 5 : slotDimensions.width * 0.2),
            tintColor:
              // TODO: remove next release
              thing.image.includes('_') ? undefined : 'white',
          }}
          source={thingImageRequire(thing.image)}
        />
      </View>
      <ThingLabel
        level={thing.levelNeed}
        small={
          [THING_TYPES.RING, THING_TYPES.ELIXIR, THING_TYPES.SCROLL].includes(thing.type) || applied
        }
        duplicateLvlImage={thing.duplicateLvlImage}
      />
      {highlightWarning && warriorThing && checkThingStability(warriorThing, thing) ? (
        <View style={styles.thingStabilityLabel} />
      ) : null}
    </View>
  );
};

function thingImage(
  warriorThing: WarriorThingType,
  thing: ThingType,
  onThingPress: Function,
  undressEnabled: boolean | undefined,
  dynamicStyles: any,
  applied: boolean = false,
  applyEnabled?: boolean,
  applicable?: boolean,
) {
  if (!warriorThing) return null;

  const WrapperComp =
    !IS_WEB || undressEnabled || (IS_WEB && applicable && applyEnabled) ? TouchableOpacity : View;
  const thingImageComp = (
    <ThingImage
      warriorThing={warriorThing}
      thing={thing}
      dynamicStyles={dynamicStyles}
      applied={applied}
    />
  );

  return (
    <WrapperComp
      onPress={() =>
        action(async () => {
          if (undressEnabled) {
            log('undressThingAction', {
              warriorLogin: heroStore.hero.login,
              warriorThingId: warriorThing.id,
            });
            await dressUndressThing(false, warriorThing.id);
          } else if (IS_WEB && applicable) {
            applyAction(thing, warriorThing);
          } else onThingPress(warriorThing, applicable && applyEnabled);
        })
      }
      onLongPress={() => {
        if (!undressEnabled) return;
        if (!IS_WEB) {
          onThingPress(warriorThing, applicable);
        } else if (applicable) {
          applyAction(thing, warriorThing);
        }
      }}
      style={styles.slotThing}
    >
      {IS_WEB ? (
        <Tooltip
          html={
            <ThingItem warrior={heroStore.hero} warriorThingOrId={warriorThing} modalInfo tooltip />
          }
          trigger="mouseenter"
          animationFill={false}
          followCursor
          animation="none"
          arrow={false}
          animateFill={false}
          onHide={() => {
            setTimeout(() => {
              const elements = document.getElementsByClassName('leave');
              [...(elements as any)].forEach((item: any) => {
                if (!item.parentElement.style.position) {
                  item.parentElement.style.display = 'none';
                }
              });
            }, 500);
          }}
          duration={0}
          hideOnClick={false}
          unmountHTMLWhenHide
        >
          {thingImageComp}
        </Tooltip>
      ) : (
        thingImageComp
      )}
    </WrapperComp>
  );
}

type Props = {
  warrior: WarriorType;
  undressEnabled?: boolean;
  onThingInfo?: Function;
  height?: number;
  hideThingOverlay?: boolean;
  applyEnabled?: boolean;
  navigation?: any;
};

type State = {
  showThingInfoModal: boolean;
};

@observer
export default class Body extends Component<Props, State> {
  state = {
    showThingInfoModal: false,
  };

  warriorThing: any = null;

  thingInfoShowApplyAction: any = false;

  didFocusSubscription: any;

  appliedThingsRef: any;

  scrollsElixirsRef: any;

  constructor(props: Props) {
    super(props);

    const { navigation } = props;
    if (IS_WEB && navigation) {
      this.didFocusSubscription = navigation.addListener('didBlur', this.resetScrolls);
    }
  }

  componentWillUnmount() {
    if (this.didFocusSubscription) this.didFocusSubscription.remove();
  }

  resetScrolls = () => {
    this.appliedThingsRef.scrollTo(0, 0);
    this.scrollsElixirsRef.scrollTo(0, 0);
  };

  onToggleThingInfo = (warriorThing: WarriorThingType, showApplyAction?: boolean) => {
    const value = !!warriorThing;
    this.warriorThing = warriorThing;
    this.thingInfoShowApplyAction = showApplyAction;
    this.setState({ showThingInfoModal: value });

    const { onThingInfo } = this.props;
    if (onThingInfo) onThingInfo(value);
  };

  render() {
    const { warrior, undressEnabled, height, hideThingOverlay, applyEnabled } = this.props;

    const { showThingInfoModal } = this.state;

    const { things } = appStore.initData;
    const dressedThings = getDressedThings(warrior);
    const appliedThings = getAppliedThings(warrior);
    let alreadyArmDressed: any;

    const rings = getDressedThingsByType(warrior, THING_TYPES.RING, things);

    const scrollsElixirs = getDressedThingsByType(
      warrior,
      [THING_TYPES.SCROLL, THING_TYPES.ELIXIR],
      things,
    );

    const ddynamicStyles = getDynamicStyles(height);
    const { ELIXIR_WIDTH, ELIXIRS_WRAPPER_WIDTH, ELIXIRS_WRAPPER_HEIGHT } = ddynamicStyles;
    const dynamicStyles: any = ddynamicStyles.dynamicStyles;

    return (
      <View style={dynamicStyles.wrapper}>
        <Image
          style={[
            dynamicStyles.wrapper,
            {
              position: 'absolute',
            },
          ]}
          resizeMode="stretch"
          source={require('../../assets/images/bigBar4.png')}
        />
        <View style={dynamicStyles.slotBody}>
          <Image
            style={Object.assign({}, dynamicStyles.avatar)}
            resizeMode="contain"
            source={warriorImageRequire(warrior.image)}
          />
        </View>

        <View style={{ position: 'absolute' }}>
          {[
            THING_TYPES.GLOVES,
            THING_TYPES.HELMET,
            THING_TYPES.AMULET,
            THING_TYPES.BRACER,
            ARMS,
            THING_TYPES.ARMOR,
            ARMS_SHIELD,
            THING_TYPES.PANTS,
            THING_TYPES.BELT,
            THING_TYPES.BOOTS,
            THING_TYPES.CAPE,
          ].map((type: string) => {
            let thing: ThingType = undefined as any;
            const warriorThing = dressedThings.find((item) => {
              thing = getThing(things, item.thing);
              const isArmResult = isArm(thing);
              if (isArmResult && !alreadyArmDressed) alreadyArmDressed = item.id;
              return (
                thing.type === type ||
                (type === ARMS && isArmResult) ||
                (type === ARMS_SHIELD && thing.type === THING_TYPES.SHIELD) ||
                (type === ARMS_SHIELD &&
                  isArmResult &&
                  alreadyArmDressed &&
                  alreadyArmDressed !== item.id)
              );
            });

            const slotDimensions = getSlotDimensions(type, ddynamicStyles);
            return (
              <View key={type} style={getSlotStyles(type, ddynamicStyles)}>
                <Image
                  style={{
                    height: slotDimensions.height,
                    width: slotDimensions.width,
                    position: 'absolute',
                  }}
                  source={getThingBackImage(type)}
                />
                <Image
                  style={{
                    height: slotDimensions.width,
                    width: slotDimensions.height,
                    position: 'absolute',
                  }}
                  source={require('../../assets/images/armWrapper.png')}
                />
                {/* $FlowFixMe */}
                {thingImage(
                  warriorThing as WarriorThingType,

                  thing,
                  this.onToggleThingInfo,
                  undressEnabled,
                  ddynamicStyles,
                )}
              </View>
            );
          })}
        </View>
        <View style={dynamicStyles.rings}>
          {range(4).map((item: number) => {
            const slotDimensions = getSlotDimensions(THING_TYPES.RING, ddynamicStyles);
            const width = slotDimensions.width;
            const position = [
              { top: 0, left: 0 },
              { top: 0, left: width },
              { top: width, left: 0 },
              { top: width, left: width },
            ][item];

            const { warriorThing, thing } = rings[item] || {};
            return (
              <View key={item} style={[dynamicStyles.ring, styles.block, position]}>
                <Image
                  style={{
                    height: width,
                    width: width,
                    position: 'absolute',
                  }}
                  source={getThingBackImage(THING_TYPES.RING)}
                />
                <Image
                  style={{
                    height: width,
                    width: width,
                    position: 'absolute',
                  }}
                  source={require('../../assets/images/armWrapper.png')}
                />
                {thingImage(
                  warriorThing,
                  thing,
                  this.onToggleThingInfo,
                  undressEnabled,
                  ddynamicStyles,
                )}
              </View>
            );
          })}
        </View>
        <View style={[dynamicStyles.elixirsWrapper, dynamicStyles.appliedWrapper]}>
          <Image
            style={[
              {
                width: ELIXIRS_WRAPPER_WIDTH,
                height: ELIXIRS_WRAPPER_HEIGHT,
                position: 'absolute',
              },
            ]}
            resizeMode="stretch"
            source={require('../../assets/images/bigBar6.png')}
          />
          <ScrollView
            horizontal
            style={dynamicStyles.elixirsScroll}
            contentContainerStyle={{
              width: appliedThings.length * ELIXIR_WIDTH,
            }}
            {...({ dataSet: { className: 'hide-native-scrollbar' } } as any)}
            ref={(ref) => {
              this.appliedThingsRef = ref;
            }}
          >
            {appliedThings.map((item: any, index: number) => {
              const thing = getThing(things, item.thing);
              return (
                <View
                  key={item.id}
                  style={[
                    styles.block,
                    dynamicStyles.elixir,
                    { top: 0, left: index * ELIXIR_WIDTH },
                  ]}
                >
                  {thingImage(item, thing, this.onToggleThingInfo, false, ddynamicStyles, true)}
                </View>
              );
            })}
          </ScrollView>
        </View>
        <View style={dynamicStyles.elixirsWrapper}>
          <Image
            style={[
              {
                width: ELIXIRS_WRAPPER_WIDTH,
                height: ELIXIRS_WRAPPER_HEIGHT,
                position: 'absolute',
              },
            ]}
            resizeMode="stretch"
            source={require('../../assets/images/bigBar6.png')}
          />
          <ScrollView
            horizontal
            style={dynamicStyles.elixirsScroll}
            contentContainerStyle={{
              width: scrollsElixirs.length * ELIXIR_WIDTH,
            }}
            ref={(ref) => {
              this.scrollsElixirsRef = ref;
            }}
            {...({ dataSet: { className: 'hide-native-scrollbar' } } as any)}
          >
            {scrollsElixirs.map((item: any, index: number) => (
              <View
                key={item.warriorThing.id}
                style={[styles.block, dynamicStyles.elixir, { top: 0, left: index * ELIXIR_WIDTH }]}
              >
                {thingImage(
                  item.warriorThing,
                  item.thing,
                  this.onToggleThingInfo,
                  undressEnabled,
                  ddynamicStyles,
                  true,
                  applyEnabled,
                  thingCanBeApplied(heroStore.hero, item.thing),
                )}
              </View>
            ))}
          </ScrollView>
        </View>
        {showThingInfoModal ? (
          <ThingInfoModal
            key={1}
            warrior={warrior}
            warriorThing={this.warriorThing}
            onHide={this.onToggleThingInfo}
            showApplyAction={this.thingInfoShowApplyAction}
            hideOverlay={hideThingOverlay}
          />
        ) : null}
      </View>
    );
  }
}
